import * as React from "react";
import { find } from "lodash";
import { RadiosEditor } from "../../../../../../react-core/Html/RadiosEditor";
import { InvestmentAllocation } from "../../../../../../Wrap/Models/Domains/Investment/InvestmentAllocation";
import { InstrumentSelection } from "../../../../../../Wrap/Models/Models/InstrumentSelection/InstrumentSelection";
import { CheckboxGroup } from "../../../../../Shared/CheckboxGroup/CheckboxGroup";
import { IProcessComponentProps, ProcessComponent } from "../../../../../Shared/Process/Process/ProcessComponent";
import { SectionHeader } from "../SharedComponents";
import { Money } from "../../../../../../Wrap/Core/DataTypes/Money";
import { GiaBase } from "../../../../../../Wrap/Models/Models/QuoteAndApplyBase/GiaBase";
import {
    getPaymentsAmountByKey,
    getTransferInAmount,
    initAmount,
} from "../../../../../Shared/IST/Components/Shared/Utils";
import { IsaBase } from "../../../../../../Wrap/Models/Models/QuoteAndApplyBase/IsaBase";
import { ProcessType } from "../../../../../Shared/IST/Resource/Content";
import { TransferISTSelection } from "../../../../../../Wrap/Models/Models/InstrumentSelection/TransferISTSelection";
import { ProductType } from "../../../../../../Wrap/Constants/Enums/Domain/ProductType";

interface IInvestmentToWithdrawalProps extends IProcessComponentProps {
    instruments: Array<InstrumentSelection>;
    model: GiaBase | IsaBase;
    keyString: string;
    type: ProcessType;
    reRegInstruments: TransferISTSelection[];
}

interface IInvestmentToWithdrawalState {}

export function calculateMoney(
    allocations: Array<InvestmentAllocation>,
    money: Money,
    reregInvestments: TransferISTSelection[],
    instruments: Array<InstrumentSelection>,
    model: GiaBase | IsaBase,
    key: string
): Array<InvestmentAllocation> {
    // Calculate total proportion
    let totalProportion = 0;
    const singlePaymentsTotalAmount = getPaymentsAmountByKey(model, [
        "ClientSinglePayment",
        "ThirdPartySinglePayment",
        "EmployerSinglePayment",
    ]);
    const regularClientPaymentsTotalAmount = getPaymentsAmountByKey(model, ["ClientRegularPayment"]);
    const regularThirdPartyPaymentsTotalAmount = getPaymentsAmountByKey(model, ["ThirdPartyRegularPayment"]);
    const regularEmployerPaymentsTotalAmount = getPaymentsAmountByKey(model, ["EmployerRegularPayment"]);
    const transferInTotalAmount = getTransferInAmount(model, key);
    allocations.forEach((e) => {
        const obj = instruments.find((o) => {
            return o.Instrument.ProductId === e.ProductId;
        });
        totalProportion += singlePaymentsTotalAmount * (obj?.Single?.Percentage || 0);
        totalProportion += regularClientPaymentsTotalAmount * (obj?.ClientRegular?.Percentage || 0);
        totalProportion += regularThirdPartyPaymentsTotalAmount * (obj?.ThirdPartyRegular?.Percentage || 0);
        totalProportion += regularEmployerPaymentsTotalAmount * (obj?.EmployerRegular?.Percentage || 0);
        totalProportion += transferInTotalAmount * (obj?.Transfer?.Percentage || 0);
        const reg = reregInvestments.find((ele) => ele.Investment.ProductId === e.ProductId);
        totalProportion += initAmount(reg?.QuotationValue);
    });

    // Prepare new allocations array
    let _totalAmount = 0;
    const newAllocations = allocations.map((e, index) => {
        const obj = find(instruments, (o) => {
            return o.Instrument.ProductId === e.ProductId;
        });
        const reg = reregInvestments.find((ele) => ele.Investment.ProductId === e.ProductId);
        const totalAmount =
            singlePaymentsTotalAmount * (obj?.Single?.Percentage || 0) +
            regularClientPaymentsTotalAmount * (obj?.ClientRegular?.Percentage || 0) +
            regularThirdPartyPaymentsTotalAmount * (obj?.ThirdPartyRegular?.Percentage || 0) +
            regularEmployerPaymentsTotalAmount * (obj?.EmployerRegular?.Percentage || 0) +
            transferInTotalAmount * (obj?.Transfer?.Percentage || 0) +
            initAmount(reg?.QuotationValue);
        const currentAmt = parseFloat((money.Amount * (totalAmount / totalProportion)).toFixed(6));
        let latestMoney = {};
        if (index === allocations.length - 1) {
            latestMoney = {
                Amount: money.Amount - _totalAmount,
                Currency: money.Currency,
            };
        } else {
            _totalAmount += currentAmt;
            latestMoney = {
                Amount: currentAmt,
                Currency: money.Currency,
            };
        }
        e.Value = latestMoney;
        return e;
    });

    return newAllocations;
}

export class InvestmentToWithdrawal extends ProcessComponent<
    IInvestmentToWithdrawalProps,
    IInvestmentToWithdrawalState
> {
    constructor(props: any) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        super.componentDidMount();
        const { data, instruments, onChange, reRegInstruments } = this.props;

        onChange({
            Allocations: !data.Allocations
                ? []
                : data.Allocations.filter(
                      (e) =>
                          instruments.some((item) => item.Instrument.ProductId == e.ProductId) ||
                          reRegInstruments.some((item) => item.Investment.ProductId == e.ProductId)
                  ),
            ProportionallyAllocated: data?.ProportionallyAllocated !== null ? data.ProportionallyAllocated : true,
        });
    }

    isValid() {
        const { data } = this.props;
        if (data.ProportionallyAllocated === false && data.Allocations.length == 0)
            {this.props.showErrorMsgPopUp(["You must select at least one specific asset."], true);}
        const flag = data.ProportionallyAllocated === false && data.Allocations.length == 0 ? false : true;
        return super.isValid() && flag;
    }

    private renderCheckboxGroup() {
        const { data, instruments, onChange, model, keyString, reRegInstruments } = this.props;
        const displayRereg = reRegInstruments.filter(
            (ele) => !instruments.some((item) => item.Instrument.ProductId == ele.Investment.ProductId)
        );
        let dataArr = [];
        // TODO Need change after function implemented
        // TODO Use mock data for now, need change when get real data
        if (instruments && instruments.length > 0) {
            dataArr = instruments.filter((item) => item.Instrument.Type != ProductType.CashProduct).map((ele, index) => {
                return {
                    label: ele?.Instrument?.Name,
                    value: data.Allocations?.some((item) => item.ProductId == ele?.Instrument?.ProductId),
                    onChange: (v) => {
                        if (v) {
                            let _data;
                            const newAllocation: InvestmentAllocation = {
                                Id: null,
                                ProductId: ele?.Instrument?.ProductId,
                                Name: ele?.Instrument?.Name,
                            };
                            if (data?.ClientRegularPayment?.Withdrawal?.Percentage) {
                                // save percentage
                                newAllocation.Percentage = data?.ClientRegularPayment?.Withdrawal?.Percentage;
                                _data = data.Allocations?.concat(newAllocation);
                            } else {
                                // calculate accurate money amoun
                                const newAllocations = data.Allocations?.concat(newAllocation);
                                _data = calculateMoney(
                                    newAllocations,
                                    data?.ClientRegularPayment?.Withdrawal?.Money,
                                    reRegInstruments,
                                    instruments,
                                    model,
                                    keyString
                                );
                            }
                            onChange({ Allocations: _data });
                        } else {
                            const newAllocations = data.Allocations?.filter(
                                (item) => item.ProductId !== ele?.Instrument?.ProductId
                            );
                            const _data = calculateMoney(
                                newAllocations,
                                data?.ClientRegularPayment?.Withdrawal?.Money,
                                reRegInstruments,
                                instruments,
                                model,
                                keyString
                            );
                            onChange({
                                Allocations: _data,
                            });
                        }
                    },
                };
            });
        } else if (displayRereg && displayRereg.length > 0) {
            dataArr = displayRereg.map((ele, index) => {
                return {
                    label: ele?.Investment?.DisplayName,
                    value: data.Allocations?.some((item) => item.ProductId == ele?.Investment?.ProductId),
                    onChange: (v) => {
                        if (v) {
                            let _data;
                            const newAllocation: InvestmentAllocation = {
                                Id: null,
                                ProductId: ele?.Investment?.ProductId,
                                Name: ele?.Investment?.DisplayName,
                            };
                            if (data?.ClientRegularPayment?.Withdrawal?.Percentage) {
                                // save percentage
                                newAllocation.Percentage = data?.ClientRegularPayment?.Withdrawal?.Percentage;
                                _data = data.Allocations?.concat(newAllocation);
                            } else {
                                // calculate accurate money amoun
                                const newAllocations = data.Allocations?.concat(newAllocation);
                                _data = calculateMoney(
                                    newAllocations,
                                    data?.ClientRegularPayment?.Withdrawal?.Money,
                                    reRegInstruments,
                                    instruments,
                                    model,
                                    keyString
                                );
                            }
                            onChange({ Allocations: _data });
                        } else {
                            const newAllocations = data.Allocations?.filter(
                                (item) => item.ProductId !== ele?.Investment?.ProductId
                            );
                            const _data = calculateMoney(
                                newAllocations,
                                data?.ClientRegularPayment?.Withdrawal?.Money,
                                reRegInstruments,
                                instruments,
                                model,
                                keyString
                            );
                            onChange({
                                Allocations: _data,
                            });
                        }
                    },
                };
            });
        }
        return <CheckboxGroup type="block" options={dataArr} />;
    }

    public render(): JSX.Element {
        const { data, onChange, type } = this.props;
        return (
            <>
                {SectionHeader(
                    "Investments to withdraw",
                    "Note - A stockbroking charge will be applied every time an equity, bond or gilt is disinvested to fund this withdrawal and it is not possible to take regular withdrawals from cash deposit investments"
                )}
                <RadiosEditor
                    cssWrapper="rg-inline mb-30"
                    selected={data?.ProportionallyAllocated}
                    options={type == ProcessType.WhatIf ? [true] : [true, false]}
                    getText={(v) => (v ? "Proportionately across all assets" : "From specified")}
                    onChange={(v) => onChange({ ProportionallyAllocated: v, Allocations: [] })}
                    required={"Please choose"}
                />

                {data.ProportionallyAllocated === (type == ProcessType.WhatIf) && (data.ProportionallyAllocated === false ? this.renderCheckboxGroup() : () => {})}
            </>
        );
    }
}
